import type { EvalsResultOpensearchLog } from '@/types/eval-v2-types';
import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';

/**
 * Workflow Evals tab data source.
 *
 * Eval results are persisted to OpenSearch by platform-api when an eval node
 * finishes a run. They are scoped by `workflow_run_id` (== agentic-backend
 * `session_id`) and tagged with the originating eval node's `node_id`.
 *
 *   GET /evaluations/test/runs-by-workflow/{orgid}/{workflow_run_id}
 *
 * Per-node filtering is applied client-side using the `node_id` field on
 * each record — the endpoint itself does not take a `node_id` query param.
 */

const orgId = (): string | undefined =>
  useOrgStore.getState().currentOrg?.orgid;

/**
 * List eval result documents for a single workflow run.
 *
 * `workflowRunId` here is the agentic-backend `session_id` (which is what the
 * eval node forwards to platform-api), not the platform `WorkflowRun.id`.
 */
export async function listEvalResultsForWorkflowRun(
  workflowRunId: string,
  signal?: AbortSignal,
): Promise<EvalsResultOpensearchLog[]> {
  const id = orgId();
  if (!id || !workflowRunId) return [];

  const url = `/evaluations/test/runs-by-workflow/${id}/${workflowRunId}`;

  const response = await apiRequest<EvalsResultOpensearchLog[]>({
    url,
    method: 'GET',
    target: 'platform',
    signal,
    suppressSentryStatuses: [404],
  });
  if (response.success === false || response.detail) {
    if (response.detail?.toLowerCase().includes('not found')) return [];
    throw new Error(response.detail || 'Failed to list eval results');
  }
  return (response.data as EvalsResultOpensearchLog[]) ?? [];
}
